'use client';

import { useMemo } from 'react';
import { useJobQueueStore } from '../model/job-queue.store';
import { useSubscriptionLimits } from '@/features/subscription/model/subscription.selectors';
import type { RenderBlockReason } from '@/types/render';

/**
 * Server-authoritative render-gate reason from the WS/REST queue aggregate.
 *
 * This is the SINGLE source of truth for whether a render may proceed — the
 * API applies priority (tokens > queue) and emits exactly one reason (or
 * `null` = allowed). The render preflight maps this straight to a paywall;
 * no client-side renderability math (token balance, subscription state, slot
 * counting) decides this. Returns `null` until the first snapshot lands
 * (cold-start optimistically allows the tap; the server 422s if it must).
 */
export function useRenderBlockReason(): RenderBlockReason {
  return useJobQueueStore((state) => state.queueAggregate?.blockReason ?? null);
}

export interface QueueLimitStatus {
  isQueueFull: boolean;
  activeCount: number;
  queueLimit: number;
  hasQueueLimit: boolean;
  message: string;
}

/**
 * Render-slot quota status for the Generate button preflight.
 *
 * Reads `queueAggregate` from the WS-driven store (computed once per snapshot
 * in API `RenderLimitsService::getQueueStatus`) when present. Falls back to the
 * local count + plan-limit math during cold-start before the first WS snapshot
 * or `GET /v1/queue/status` lands. The local fallback never sees albums (they
 * have their own sliding-window quota and must not consume render slots).
 *
 * `isQueueFull` gates only on slot availability — a `canRender=false` due to
 * insufficient tokens is surfaced through the separate token-balance check,
 * not the queue-full paywall.
 */
export function useQueueLimit(): QueueLimitStatus {
  const queueAggregate = useJobQueueStore((state) => state.queueAggregate);
  const getProcessingRenderJobCount = useJobQueueStore(
    (state) => state.getProcessingRenderJobCount
  );
  const jobQueue = useJobQueueStore((state) => state.jobQueue);
  const limits = useSubscriptionLimits();

  const localActiveCount = useMemo(
    () => getProcessingRenderJobCount(),
    [getProcessingRenderJobCount, jobQueue]
  );
  const localPlanLimit = limits?.generations_queue || 0;

  const activeCount = queueAggregate?.activeRenders ?? localActiveCount;
  const queueLimit = queueAggregate?.queueLimit ?? localPlanLimit;
  const hasQueueLimit = queueLimit > 0;
  const isQueueFull = queueAggregate
    ? queueAggregate.availableSlots === 0
    : hasQueueLimit && activeCount >= queueLimit;

  const message = useMemo(() => {
    if (!hasQueueLimit) {
      return '';
    }
    if (isQueueFull) {
      return `Queue full (${activeCount}/${queueLimit}). Please wait for current renders to complete.`;
    }
    return `Queue: ${activeCount}/${queueLimit}`;
  }, [isQueueFull, activeCount, queueLimit, hasQueueLimit]);

  return {
    isQueueFull,
    activeCount,
    queueLimit,
    hasQueueLimit,
    message,
  };
}
